import { useState } from 'react';
import type { FulfilVoterDataOrderBody } from '@signalvote/api-client';
import type { FulfilmentFilter, FulfilmentRecipe, VoterDataOrderDto } from '@signalvote/shared';
import { deliveredCountAllowance, deliveredCountNeedsConfirmation } from '@signalvote/shared';
import { PendingButton } from '@/components/ui';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VoterDataExportStatus } from './VoterDataExportStatus';

/**
 * The fulfilment recipe, as a platform admin rebuilds the list in L2's web UI: the app, every filter
 * as its L2 column and raw values, one per household, the columns to export, and the quoted counts
 * to check the rebuild against. The API sends the recipe to platform admins only.
 */
export function FulfilmentRecipeView({ recipe }: { recipe: FulfilmentRecipe }) {
  return (
    <div data-testid="fulfilment-recipe" className="space-y-3 rounded-md border border-input bg-muted/30 p-3 text-sm">
      <p className="text-xs font-medium text-muted-foreground">Fulfilment recipe</p>

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <dt className="text-muted-foreground">L2 app</dt>
        <dd className="font-mono">{recipe.app}</dd>
        {recipe.filters.map((filter, i) => (
          <FilterRow key={`${filter.column}-${i}`} filter={filter} />
        ))}
        <dt className="text-muted-foreground">One per household</dt>
        <dd data-testid="recipe-one-per-household">{recipe.onePerHousehold ? 'Yes' : 'No'}</dd>
      </dl>

      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Columns to export</p>
        <p data-testid="recipe-columns" className="break-words font-mono text-xs">
          {recipe.columns.map((c) => c.column).join(', ')}
        </p>
      </div>

      <p data-testid="recipe-quoted" className="tabular-nums">
        Quoted: {recipe.quotedCount.toLocaleString()} voters in {recipe.quotedHouseholds.toLocaleString()} households
      </p>
    </div>
  );
}

function FilterRow({ filter }: { filter: FulfilmentFilter }) {
  // A vote-history filter keeps voted (1) or didn't vote (0); every other filter keeps raw values.
  const value = filter.values ? filter.values.join(', ') : String(filter.voted);
  return (
    <>
      <dt className="font-mono text-muted-foreground">{filter.column}</dt>
      <dd data-testid={`recipe-filter-${filter.column}`} className="break-words font-mono">
        {value}
      </dd>
    </>
  );
}

/** The fulfil body minus the concurrency token, which the container adds from the order it read. */
export type FulfilVoterDataInput = Omit<FulfilVoterDataOrderBody, 'expectedConcurrencyVersion'>;

interface VoterDataFulfilFormProps {
  /** What the customer paid for: households when one per household, otherwise voters. */
  quotedRecords: number;
  /** The automatic export's progress. Null fields (anyone but a platform admin) show nothing. */
  exportProgress: Pick<VoterDataOrderDto, 'exportState' | 'exportHoldReason' | 'exportHoldDetail'>;
  onFulfil: (input: FulfilVoterDataInput) => void;
  fulfilling?: boolean;
  error?: string | null;
}

/**
 * Attach the CSV exported from L2 and record how many records it holds. A count further from the
 * quote than the shared allowance (5%, capped at $50 of records) can't be delivered until the admin
 * confirms they contacted the customer; the server enforces the same rule and checks the file's columns.
 */
export function VoterDataFulfilForm({ quotedRecords, exportProgress, onFulfil, fulfilling = false, error }: VoterDataFulfilFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [countText, setCountText] = useState('');
  const [customerContacted, setCustomerContacted] = useState(false);

  const deliveredCount = /^\d+$/.test(countText) ? Number(countText) : null;
  const needsConfirmation =
    deliveredCount !== null && deliveredCountNeedsConfirmation(quotedRecords, deliveredCount);
  const canSubmit =
    !!file && deliveredCount !== null && deliveredCount > 0 && (!needsConfirmation || customerContacted);

  const submit = () => {
    if (!canSubmit) return;
    onFulfil({ file: file!, deliveredCount: deliveredCount!, customerContacted: needsConfirmation && customerContacted });
  };

  return (
    <div className="space-y-3 border-t pt-4">
      <p className="text-sm font-medium">Deliver the list</p>

      {exportProgress.exportState && (
        <VoterDataExportStatus
          state={exportProgress.exportState}
          holdReason={exportProgress.exportHoldReason}
          holdDetail={exportProgress.exportHoldDetail}
        />
      )}

      <div className="space-y-1">
        <Label htmlFor="fulfil-file">CSV exported from L2</Label>
        <Input
          id="fulfil-file"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="fulfil-count">Delivered count</Label>
        <Input
          id="fulfil-count"
          inputMode="numeric"
          value={countText}
          onChange={(e) => setCountText(e.target.value.trim())}
        />
        <p className="text-xs text-muted-foreground tabular-nums">
          The customer paid for {quotedRecords.toLocaleString()} records.
        </p>
      </div>

      {needsConfirmation && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950">
          <p data-testid="delivered-count-warning">
            {deliveredCount!.toLocaleString()} records is more than{' '}
            {deliveredCountAllowance(quotedRecords).toLocaleString()} records away from the{' '}
            {quotedRecords.toLocaleString()} the customer paid for. Contact them before delivering it.
          </p>
          <div className="flex items-center gap-2">
            <Checkbox
              id="fulfil-contacted"
              checked={customerContacted}
              onCheckedChange={(checked) => setCustomerContacted(checked === true)}
            />
            <Label htmlFor="fulfil-contacted">I contacted the customer about the difference</Label>
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <PendingButton className="w-full" onClick={submit} disabled={!canSubmit} pending={fulfilling}>
        Complete order
      </PendingButton>
    </div>
  );
}
